import { useCallback, useEffect, useRef, useState } from 'react'

// Dual-handle range slider for trimming a clip from a longer source.
//
// Props:
//   minMs, maxMs            — hard bounds (full source duration)
//   viewMinMs, viewMaxMs    — optional VISIBLE range. When set, the track
//                             renders [viewMinMs, viewMaxMs] instead of
//                             [minMs, maxMs], so per-pixel resolution
//                             scales with how zoomed-in the view is. The
//                             handles still respect [minMs, maxMs] as
//                             absolute bounds — view is purely display.
//                             Defaults to [minMs, maxMs] (no zoom).
//   startMs, endMs          — current trim window
//   onChange({startMs, endMs}) — fires while dragging (throttled by browser raf)
//   onChangeCommitted({...}) — fires once when the drag ends (use for save-on-release)
//
// Behaviour:
//   - Drag the in-handle (left) to set start; drag the out-handle (right) to set end.
//   - Click-and-drag the highlighted region between handles to translate the
//     whole window without changing its duration.
//   - Handles can't cross. Minimum window enforced to ~250ms so the slider
//     never collapses to a useless zero-width region.
//   - When start or end is outside the visible view range, the corresponding
//     handle is clamped to the view edge with a different visual style so
//     the editor knows there's content off-screen.

const MIN_WINDOW_MS = 250

function fmt(ms) {
  if (ms == null) return '0:00'
  const s = Math.max(0, Math.floor(ms / 1000))
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${m}:${String(r).padStart(2, '0')}`
}

export default function TrimRangeSlider({
  minMs = 0,
  maxMs,
  viewMinMs,
  viewMaxMs,
  startMs,
  endMs,
  playheadMs,
  onChange,
  onChangeCommitted,
  onDragStart,
  onSeek,
  height = 36,
}) {
  const trackRef = useRef(null)
  const movedRef = useRef(false)          // true once a drag actually moved → suppresses the click-to-seek
  const [drag, setDrag] = useState(null)  // null | { mode, downX, startMs, endMs }

  // ABSOLUTE span (used for handle clamping). VIEW span (used for pixel↔ms
  // mapping) defaults to the absolute span when caller hasn't zoomed in.
  const span = maxMs - minMs
  const vMin = viewMinMs != null ? viewMinMs : minMs
  const vMax = viewMaxMs != null ? viewMaxMs : maxMs
  const viewSpan = Math.max(1, vMax - vMin)
  const pctOf = (ms) => ((ms - vMin) / viewSpan) * 100
  const msOfX = useCallback((clientX) => {
    const rect = trackRef.current?.getBoundingClientRect()
    if (!rect || rect.width === 0) return startMs
    const x = Math.min(rect.right, Math.max(rect.left, clientX))
    const frac = (x - rect.left) / rect.width
    return vMin + frac * viewSpan
  }, [vMin, viewSpan, startMs])

  const clamp = (s, e) => {
    let ns = Math.round(s), ne = Math.round(e)
    if (ns < minMs) { const d = minMs - ns; ns += d; ne += d }
    if (ne > maxMs) { const d = ne - maxMs; ne -= d; ns -= d }
    if (ne - ns < MIN_WINDOW_MS) ne = ns + MIN_WINDOW_MS
    if (ns < minMs) ns = minMs
    if (ne > maxMs) ne = maxMs
    return [ns, ne]
  }

  useEffect(() => {
    if (!drag) return
    const onMove = (ev) => {
      movedRef.current = true // a real drag — not a click-to-seek
      const cx = ev.touches ? ev.touches[0].clientX : ev.clientX
      const newMs = msOfX(cx)
      if (drag.mode === 'start') {
        const ns = Math.max(minMs, Math.min(endMs - MIN_WINDOW_MS, newMs))
        onChange?.({ startMs: Math.round(ns), endMs })
      } else if (drag.mode === 'end') {
        const ne = Math.min(maxMs, Math.max(startMs + MIN_WINDOW_MS, newMs))
        onChange?.({ startMs, endMs: Math.round(ne) })
      } else if (drag.mode === 'region') {
        const deltaMs = newMs - drag.anchorMs
        const [ns, ne] = clamp(drag.startMs + deltaMs, drag.endMs + deltaMs)
        onChange?.({ startMs: ns, endMs: ne })
      }
    }
    const onUp = () => {
      setDrag(null)
      onChangeCommitted?.({ startMs, endMs })
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    window.addEventListener('touchmove', onMove)
    window.addEventListener('touchend', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      window.removeEventListener('touchmove', onMove)
      window.removeEventListener('touchend', onUp)
    }
  }, [drag, startMs, endMs, minMs, maxMs, span, onChange, onChangeCommitted, msOfX])

  const startDrag = (mode) => (ev) => {
    ev.preventDefault()
    movedRef.current = false
    const cx = ev.touches ? ev.touches[0].clientX : ev.clientX
    onDragStart?.({ mode, startMs, endMs })
    setDrag({ mode, downX: cx, anchorMs: msOfX(cx), startMs, endMs })
  }

  // Click-to-seek: a click (not a drag) anywhere on the track moves the playhead to that point,
  // clamped between the In/Out handles. Fires after mouseup; movedRef distinguishes a real drag
  // (handle/region) from a plain click. Clicks on handles that didn't drag just seek to that edge.
  const handleTrackClick = (ev) => {
    if (movedRef.current) { movedRef.current = false; return }
    if (!onSeek) return
    const ms = Math.max(startMs, Math.min(endMs, Math.round(msOfX(ev.clientX))))
    onSeek(ms)
  }

  // Visual percentages clamped to [0, 100] so handles that sit outside the
  // view range render at the edge of the track.
  const sPctRaw = pctOf(startMs)
  const ePctRaw = pctOf(endMs)
  const sPct = Math.max(0, Math.min(100, sPctRaw))
  const ePct = Math.max(0, Math.min(100, ePctRaw))
  const startOffView = sPctRaw < 0 || sPctRaw > 100
  const endOffView = ePctRaw < 0 || ePctRaw > 100
  const zoomed = viewMinMs != null && (vMin > minMs || vMax < maxMs)
  // Playhead indicator — vertical line at the current player time. Hidden
  // when the playhead is outside the visible view (zoomed in) so it doesn't
  // pin to an edge and lie about its position.
  const playheadPctRaw = playheadMs != null ? pctOf(playheadMs) : null
  const playheadVisible = playheadPctRaw != null && playheadPctRaw >= 0 && playheadPctRaw <= 100
  const playheadInsideClip = playheadMs != null && playheadMs >= startMs && playheadMs <= endMs

  return (
    <div style={{ width: '100%', userSelect: 'none' }}>
      <div ref={trackRef}
        onClick={handleTrackClick}
        style={{
          position: 'relative', height, background: '#0d1622',
          border: '1px solid #2a2a2a', borderRadius: 4, cursor: 'pointer',
        }}>
        {/* Selected region — draggable as a whole */}
        <div
          onMouseDown={startDrag('region')}
          onTouchStart={startDrag('region')}
          style={{
            position: 'absolute', top: 0, bottom: 0,
            left: `${sPct}%`, width: `${ePct - sPct}%`,
            background: 'rgba(59,130,246,0.25)',
            borderLeft: '2px solid #3b82f6', borderRight: '2px solid #3b82f6',
            cursor: 'grab',
          }}
        />
        {/* Start handle — dimmed + marked when it's actually outside the view */}
        <div
          onMouseDown={startDrag('start')}
          onTouchStart={startDrag('start')}
          title={startOffView ? `In point at ${fmt(startMs)} (off-screen — zoom out to see it)` : `In: ${fmt(startMs)}`}
          style={{
            position: 'absolute', top: -4, bottom: -4,
            left: `calc(${sPct}% - 7px)`, width: 14, borderRadius: 3,
            background: startOffView ? '#1e3a8a' : '#3b82f6', cursor: 'ew-resize',
            boxShadow: '0 0 0 1px #1e3a8a',
            opacity: startOffView ? 0.6 : 1,
          }}
        />
        {/* End handle */}
        <div
          onMouseDown={startDrag('end')}
          onTouchStart={startDrag('end')}
          title={endOffView ? `Out point at ${fmt(endMs)} (off-screen — zoom out to see it)` : `Out: ${fmt(endMs)}`}
          style={{
            position: 'absolute', top: -4, bottom: -4,
            left: `calc(${ePct}% - 7px)`, width: 14, borderRadius: 3,
            background: endOffView ? '#1e3a8a' : '#3b82f6', cursor: 'ew-resize',
            boxShadow: '0 0 0 1px #1e3a8a',
            opacity: endOffView ? 0.6 : 1,
          }}
        />
        {/* Playhead indicator — thin vertical line at current player time.
            Dimmed when outside the clip's trim window so it doesn't compete
            with the In/Out handles for attention. pointerEvents:none so it
            never intercepts clicks on the underlying region/handles. */}
        {playheadVisible && (
          <div
            title={`Playhead: ${fmt(playheadMs)}`}
            style={{
              position: 'absolute', top: -2, bottom: -2,
              left: `calc(${playheadPctRaw}% - 1px)`, width: 2,
              background: playheadInsideClip ? '#fbbf24' : '#9ca3af',
              boxShadow: playheadInsideClip ? '0 0 4px rgba(251,191,36,0.6)' : 'none',
              opacity: playheadInsideClip ? 1 : 0.55,
              pointerEvents: 'none',
              borderRadius: 1,
            }}
          />
        )}
      </div>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        marginTop: 6, fontSize: 11, color: '#888', fontFamily: 'monospace',
      }}>
        <span title="In point — actual time in source">{fmt(startMs)}</span>
        <span style={{ color: '#bbb' }}>{fmt(endMs - startMs)} clip</span>
        <span title="Out point / source duration">{fmt(endMs)} / {fmt(maxMs)}</span>
      </div>
      {zoomed && (
        <div style={{
          marginTop: 2, fontSize: 10, color: '#7dd3fc', fontFamily: 'monospace',
          display: 'flex', justifyContent: 'space-between', opacity: 0.85,
        }}>
          <span>view: {fmt(vMin)}</span>
          <span>{((vMax - vMin) / 1000).toFixed(1)}s window</span>
          <span>view: {fmt(vMax)}</span>
        </div>
      )}
    </div>
  )
}
